import { DatePicker } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { FC, useRef, useState } from 'react';
import 'moment/locale/en-gb';
import 'moment/locale/lt';
import 'moment/locale/ro';
import InputMask from 'react-input-mask';
import classNames from 'classnames';

import { useTranslate } from 'hooks';

import { LOCALES } from 'utils/constants';
import { language } from 'utils/i18n';

import './index.scss';

type DateFieldProps = {
	maxDate: Moment;
	onChange?: (v: string) => moment.Moment | null;
	label: string;
	id?: string;
};

export const DateField: FC<DateFieldProps> = ({ maxDate, onChange, label, id }) => {
	const ref = useRef<HTMLInputElement>(null);

	const [isOpen, setIsOpen] = useState(false);
	const [inputValue, setInputValue] = useState('');

	const { dateFieldYearText, dateFieldMonthText, dateFieldDayText } = useTranslate([
		'dateFieldYearText',
		'dateFieldMonthText',
		'dateFieldDayText',
	]);

	const setInputRef = (element: HTMLInputElement | null) => {
		if (!ref.current) {
			if (element) {
				// @ts-ignore
				ref.current = element;
			}
		}
	};

	return (
		<div className="dateField">
			<DatePicker
				locale={(LOCALES as any)[language]}
				defaultPickerValue={maxDate}
				format="YYYY/MM/DD"
				autoComplete="on"
				disabledDate={(current: Moment) => current > maxDate}
				showToday={false}
				open={isOpen}
				allowClear={false}
				suffixIcon={null}
				inputRender={({ onChange: onInputChange }) => {
					return (
						<InputMask
							id={id}
							inputRef={(el) => setInputRef(el)}
							inputMode="numeric"
							mask="9999/99/99"
							value={inputValue}
							alwaysShowMask={false}
							// @ts-ignore
							maskChar={null}
							onBlur={(e) => {
								setIsOpen(false);
								if (onInputChange) {
									onInputChange(e);
								}
							}}
							onFocus={(e) => {
								if (onInputChange) {
									onInputChange(e);
								}
								setIsOpen(true);
							}}
							onChange={(e: any) => {
								const v = e.target.value;
								setInputValue(v);
								if (onInputChange) {
									onInputChange(e);
								}
								if (onChange) {
									onChange(moment(v).format('YYYY-MM-DD'));
								}
							}}
						/>
					);
				}}
				onChange={(e) => {
					if (onChange) {
						const date = moment(e);
						setInputValue(date.isValid() ? moment(e).format('YYYY/MM/DD') : '');
						onChange(moment(e).format('YYYY-MM-DD'));

						if (ref.current && ref.current.blur) {
							ref.current.blur();
							setIsOpen(false);
						}
					}
				}}
			/>

			<div className="dateField__placeholder">
				<PlaceholderPart length={0} value={dateFieldYearText} input={inputValue} />
				&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
				<PlaceholderPart length={4} value="/" input={inputValue} />
				&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
				<PlaceholderPart length={5} value={dateFieldMonthText} input={inputValue} />
				&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
				<PlaceholderPart length={7} value="/" input={inputValue} />
				&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
				<PlaceholderPart length={8} value={dateFieldDayText} input={inputValue} />
			</div>

			<div className="dateField__icon" role="button" onClick={() => setIsOpen((prev) => !prev)}>
				<CalendarOutlined />
			</div>

			<span className="dateField__label">{label}</span>
		</div>
	);
};

const PlaceholderPart: FC<{ length: number; value: string; input: string }> = ({
	length,
	value,
	input,
}) => {
	return (
		<span className={classNames({ dateField__placeholder_white: input.length > length })}>
			{value}
		</span>
	);
};
